import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { FaFilter } from 'react-icons/fa';
import { filters } from '@/constants/filterConfigs';
import { SOSCheckBoxes } from '@/components/inputs/check-box';
import { SOSMultiSelectCheckboxDropDown } from '@/components/inputs/multi-select-checkbox-dropdown';
import { useFetchQuery } from '@/hooks/useFetchQuery';
import { fetchReleaseManagementFiltersApi } from '@/services/releaseServices';
import { addReleases } from '@/pages/releases/release-list/releaseListSlice';
import { FiltersContainer } from '@/layout/common-filter/styles/Filters.styles';
import {
  useBaseFilter,
  convertFinalKey,
  DEFAULT_COMPLIANCE_VALUES,
} from './BaseFilter';

/**
 * Shared field rendering for the page-specific release filter components.
 * @param {string} props.page - Page identifier (RELEASE_LIST_PAGE or RELEASE_CALENDAR_PAGE)
 * @param {Object} props.filterActions - Actions from the page-specific slice
 * @return {JSX.Element} Release filter component
 */
export const ReleaseFilter = ({
  page,
  filterActions: { addReleaseFilters, clearReleaseFilters },
}) => {
  const filterData = filters[page];

  // Fetch filter options from API
  const { data } = useFetchQuery({
    currNav: '/releases/filters',
    qryFn: () => fetchReleaseManagementFiltersApi(),
    key: 'filterRelease',
  });

  // Use base filter hook
  const {
    dispatch,
    releaseFiltersObj,
    reff,
    setReff,
    relStatusVal,
    relDatesVal,
    archiveVal,
    defaultComplianceValue,
    initialRelStatusVal,
    mergedData,
    activeFilterCount,
  } = useBaseFilter({ page, filterData, apiData: data });

  /**
   * Handle filter change
   */
  const handleChange = useCallback(
    (e, filter) => {
      const key = convertFinalKey(filter.name);
      const value = key === 'allReleases' ? (e.target.checked ? 1 : 0) : e;

      // Prevent unchecking all checkbox options
      const isCheckboxFilter = [
        'releaseStatusCodes',
        'releaseDatesCodes',
        'archiveStatusCodes',
      ].includes(key);
      if (isCheckboxFilter && e.length === 0) return;

      if (page === 'RELEASE_LIST_PAGE') {
        dispatch(addReleases([]));
      }

      dispatch(addReleaseFilters({ key, value }));
    },
    [page, dispatch, addReleaseFilters],
  );

  /**
   * Clear all filters
   */
  const clearFilterData = useCallback(() => {
    dispatch(
      clearReleaseFilters({
        releaseName: '',
        jiraProjectIds: [],
        createdByIds: [],
        applicationIds: [],
        releaseStatusCodes:
          initialRelStatusVal.length > 0 ? initialRelStatusVal : [1, 2],
        releaseDatesCodes: [1, 2],
        archiveStatusCodes: [1],
        ...(page === 'RELEASE_CALENDAR_PAGE'
          ? { complianceStatus: DEFAULT_COMPLIANCE_VALUES }
          : {}),
      }),
    );
    setReff(false);
    setTimeout(() => setReff(true), 1);
  }, [dispatch, initialRelStatusVal, setReff, clearReleaseFilters, page]);

  /**
   * Render filter element
   */
  const renderElement = useCallback(
    (filter) => {
      const filterKey = convertFinalKey(filter.name);
      const currentValue = releaseFiltersObj[filterKey];

      switch (filter.type) {
        case 'DROP_DOWN_WITH_FILTER':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSMultiSelectCheckboxDropDown
                handleChange={(values) => handleChange(values, filter)}
                placeholder={filter.actualName}
                options={filter.options}
                defaultValue={currentValue || []}
                key={filter.key}
              />
            </div>
          );

        case 'RELEASE_STATUS_CHECKBOXES':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSCheckBoxes
                handleChange={(e) => handleChange(e, filter)}
                options={filter.options}
                defaultValue={relStatusVal}
                key={`${filter.key}-${relStatusVal.join(',')}`}
              />
            </div>
          );

        case 'RELEASE_DATES_CHECKBOXES':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSCheckBoxes
                handleChange={(e) => handleChange(e, filter)}
                options={filter.options}
                defaultValue={relDatesVal}
                key={`${filter.key}-${relDatesVal.join(',')}`}
              />
            </div>
          );

        case 'ARCHIVE_STATUS_CHECKBOXES':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSCheckBoxes
                handleChange={(e) => handleChange(e, filter)}
                options={filter.options}
                defaultValue={archiveVal}
                key={`${filter.key}-${archiveVal.join(',')}`}
              />
            </div>
          );

        case 'COMPLIANCE_STATUS_CHECKBOXES':
          return (
            <div className="filter-item" key={filter.name}>
              <span className="title">{filter.actualName || filter.name}</span>
              <SOSCheckBoxes
                handleChange={(e) => handleChange(e, filter)}
                options={filter.options}
                defaultValue={defaultComplianceValue}
                key={`${filter.key}-${defaultComplianceValue.join(',')}`}
              />
            </div>
          );

        default:
          return null;
      }
    },
    [
      handleChange,
      relStatusVal,
      relDatesVal,
      archiveVal,
      defaultComplianceValue,
      releaseFiltersObj,
    ],
  );

  const pageTitle =
    page === 'RELEASE_LIST_PAGE' ? 'Release Filters' : 'Calendar Filters';

  return (
    <FiltersContainer>
      {reff && (
        <>
          <div className="filter-header">
            <h3>
              <span className="header-text">
                <FaFilter className="filter-icon" />
                {pageTitle}
              </span>
              {activeFilterCount > 0 && (
                <span className="filter-count">{activeFilterCount}</span>
              )}
            </h3>
          </div>

          <div className="filters-content">
            {mergedData.map((filter) => renderElement(filter))}
          </div>

          <div className="filter-actions">
            <Button
              type="primary"
              danger
              size="large"
              onClick={clearFilterData}
            >
              Clear All Filters
            </Button>
          </div>
        </>
      )}
    </FiltersContainer>
  );
};

ReleaseFilter.propTypes = {
  page: PropTypes.string.isRequired,
  filterActions: PropTypes.shape({
    addReleaseFilters: PropTypes.func.isRequired,
    clearReleaseFilters: PropTypes.func.isRequired,
  }).isRequired,
};
